/**
 * SiteAdminLayout — dedicated shell for the public-site CMS (`/admin`).
 *
 * Independent of the LMS Layout and PodLayout. Used by super_admin / admin to
 * manage the public-facing site (landing page copy, programmes catalogue,
 * gallery, team, testimonials). Its menu deliberately does NOT include
 * Courses, Users, Payments, POD admin etc. — those live in the LMS shell at
 * /dashboard.
 */
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  House, GraduationCap, Image as ImageIcon, Article,
  ArrowSquareOut, SignOut, ShieldCheck, ArrowLeft,
} from "@phosphor-icons/react";

const SBL_LOGO = "/sbl-logo.jpeg";

const NAV = [
  { label: "Overview", icon: House, hash: "#overview" },
  { label: "Site Content", icon: Article, hash: "#content" },
  { label: "Programmes Catalogue", icon: GraduationCap, hash: "#programmes" },
  { label: "Universal Sections", icon: Article, hash: "#universal" },
  { label: "Gallery", icon: ImageIcon, hash: "#gallery" },
];

export default function SiteAdminLayout({ children, activeTab, onTabChange }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleNav = (item) => {
    const key = item.hash.slice(1);
    if (onTabChange) onTabChange(key);
    // also push hash so deep-links work
    window.history.replaceState({}, "", `/admin${item.hash}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="site-admin-layout">
      <aside className="hidden md:flex md:flex-col md:w-64 bg-slate-900 text-white">
        <div className="px-6 py-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2">
            <img src={SBL_LOGO} alt="SBL" className="w-8 h-8 object-contain rounded-sm" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400 font-bold">Site Admin</p>
              <p className="text-base font-extrabold leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>School Builder Lab Africa</p>
            </div>
          </Link>
          <p className="text-[11px] text-white/60 mt-2">Public-site Content Management</p>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1">
          {NAV.map(it => {
            const Icon = it.icon;
            const active = activeTab === it.hash.slice(1);
            return (
              <button
                key={it.hash}
                onClick={() => handleNav(it)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-all border-l-2 text-left ${
                  active
                    ? "bg-white/10 text-amber-400 border-amber-400"
                    : "text-white/80 hover:bg-white/5 hover:text-white border-transparent"
                }`}
                data-testid={`site-admin-nav-${it.hash.slice(1)}`}
              >
                <Icon size={18} weight={active ? "fill" : "regular"} />
                {it.label}
              </button>
            );
          })}

          <div className="pt-4 mt-4 border-t border-white/10 space-y-1">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5 transition-all border-l-2 border-transparent"
              data-testid="site-admin-view-public"
            >
              <ArrowSquareOut size={18} /> Return to Public Site
            </a>
            <Link
              to="/dashboard"
              className="flex items-center gap-3 px-3 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5 transition-all border-l-2 border-transparent"
              data-testid="site-admin-back-to-lms"
            >
              <ArrowLeft size={18} /> Return to User Dashboard
            </Link>
          </div>
        </nav>

        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-amber-400 text-slate-950 flex items-center justify-center text-xs font-bold">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold truncate">{user?.name}</p>
              <p className="text-[10px] text-white/50 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/70 hover:text-white hover:bg-white/5 transition-all"
            data-testid="site-admin-logout"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-600 font-bold">Site Admin · CMS</p>
            <p className="text-sm font-bold text-slate-950 capitalize">{activeTab || "Overview"}</p>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-slate-200 hover:border-amber-400 hover:text-amber-700 transition-colors"
              data-testid="site-admin-header-public"
            >
              <ArrowSquareOut size={12} /> Return to Site
            </a>
            <Link
              to="/dashboard"
              className="hidden sm:inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-slate-200 hover:border-amber-400 hover:text-amber-700 transition-colors"
              data-testid="site-admin-header-dashboard"
            >
              <ArrowLeft size={12} /> User Dashboard
            </Link>
            <div className="text-right">
              <p className="text-[11px] text-slate-500 hidden md:block">Edits publish immediately to {window.location.host}</p>
              <div className="flex items-center justify-end gap-1 text-[10px] text-emerald-700 font-bold uppercase tracking-widest mt-0.5">
                <ShieldCheck size={12} weight="fill" /> {user?.role}
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-slate-900 text-white border-t border-white/10 flex justify-around py-2 z-40">
        {NAV.slice(0, 5).map(it => {
          const Icon = it.icon;
          const active = activeTab === it.hash.slice(1);
          return (
            <button
              key={it.hash}
              onClick={() => handleNav(it)}
              className={`flex flex-col items-center px-2 py-1 text-[10px] ${active ? "text-amber-400" : "text-white/70"}`}
            >
              <Icon size={18} weight={active ? "fill" : "regular"} />
              <span className="mt-0.5">{it.label.split(" ")[0]}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
